import { Component, OnInit, signal } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { PropertyService } from '../../../_services/properties/property.service';
import { InrShortPipe } from '../../../_pipes/indian-currency.pipe';
import { KeyValuePipe, NgFor, NgIf } from '@angular/common';
import { AdvisorService } from '../../../_services/advisor/advisor.service';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderService } from '../../../_services/loader.service';
import { ToastService } from '../../../_services/toast.service';

interface Property {
  _id: string;
  name: string;
  builder: string;
  price: number;
  priceUnit: string;
  propertyType: string;
  bedrooms: number;
  landArea: number;
  location: string;
  shortDescription: string;
  isSoldOut: boolean;
  isLiked: boolean;
  galleryImage: any;
}

interface RolePermissions {
  [key: string]: boolean;
}

interface Role {
  id: string;
  label: string;
  icon: string;
  permissions: RolePermissions;
}

@Component({
  selector: 'app-add-advisor',
  imports: [NgFor, NgIf, FormsModule, ReactiveFormsModule, KeyValuePipe],
  templateUrl: './add-advisor.component.html',
  styleUrl: './add-advisor.component.scss',
})
export class AddAdvisorComponent implements OnInit {
  filteredProperties: Property[] = [];
  properties: Property[] = [];
  advisorForm!: FormGroup;
  selectedProperties: Property[] = [];
  imagePreview: string | ArrayBuffer | null = null;
  isOnboard = signal(false);
  advisor = signal({});
  roles: Role[] = [
    {
      id: 'super_admin',
      label: 'Super Admin',
      icon: 'shield-check',
      permissions: {
        canManageUsers: true,
        canDeleteProperties: true,
        canViewFinance: true,
        canAddProperties: true,
        canShowAllProperties: true,
        canOnboardAdvisor: true,
        canDeleteAdvisor: true,
        canEditSettings: true,
        CanShareProperties: true,
      },
    },
    {
      id: 'admin',
      label: 'Admin',
      icon: 'user-group',
      permissions: {
        canManageUsers: true,
        canDeleteProperties: true,
        canViewFinance: true,
        canEditSettings: false,
        canAddProperties: true,
        canShowAllProperties: true,
        canOnboardAdvisor: true,
        canDeleteAdvisor: true,
        CanShareProperties: true,
      },
    },
    {
      id: 'property_manager',
      label: 'Property Manager',
      icon: 'home',
      permissions: {
        canManageUsers: false,
        canDeleteProperties: true,
        canViewFinance: false,
        canEditSettings: false,
        canAddProperties: true,
        canShowAllProperties: true,
        canOnboardAdvisor: false,
        canDeleteAdvisor: false,
        CanShareProperties: false,
      },
    },
    {
      id: 'sales_advisor',
      label: 'Sales Advisor',
      icon: 'chart-bar',
      permissions: {
        canManageUsers: true,
        canDeleteProperties: false,
        canViewFinance: false,
        canEditSettings: false,
        canAddProperties: false,
        canShowAllProperties: true,
        canOnboardAdvisor: false,
        canDeleteAdvisor: false,
        CanShareProperties: true,
      },
    },
    // {
    //   id: 'user',
    //   label: 'User',
    //   icon: 'user',
    //   permissions: {
    //     canManageUsers: false,
    //     canDeleteProperties: false,
    //     canViewFinance: false,
    //     canEditSettings: false,
    //     canAddProperties: false,
    //     canShowAllProperties: false,
    //     canOnboardAdvisor: false,
    //     canDeleteAdvisor: false,
    //     CanShareProperties: false
    //   },
    // },
  ];
  selectedRole = this.roles[3];
  advisorId: any = null;
  mode:any = null
  constructor(
    private readonly _PS: PropertyService,
    private fb: FormBuilder,
    private readonly _AS: AdvisorService,
    private readonly loaderService: LoaderService,
    private route: Router,
    private router: ActivatedRoute,
    private _NS : ToastService
  ) {}

  ngOnInit(): void {
    this.getAllProperties();
    this.initForm();
    this.advisorId = this.router.snapshot.queryParamMap.get('id');
    this.mode = this.router.snapshot.queryParamMap.get('mode');
    if (this.advisorId) {
      this.getAdvisor(this.advisorId);
    }
  }

  async getAdvisor(id: any) {
    this.loaderService.show();
    this._AS.getAdvisor(id).subscribe(async (res: any) => {
      this.advisor.set(res.data);
      this.loadAdvisor()
      this.loaderService.hide();
    });
  }

  getAllProperties() {
    this._PS.properties().subscribe((res: any) => {
      this.properties = res.data;
      this.filteredProperties = this.properties;
    });
  }

  initForm() {
    this.advisorForm = this.fb.group(
      {
        name: ['', [Validators.required, Validators.minLength(3)]],
        email: ['', [Validators.required, Validators.email]],
        phone: [
          null,
          [Validators.required, Validators.pattern('^[0-9]{10,12}$')],
        ],
        // priceUnit: ['INR'],
        alternativePhone: ['', [Validators.pattern('^[0-9]{10,12}$')]],
        specialization: ['Residential Advisor', [Validators.required]],
        experience: ['', [Validators.required, Validators.pattern('^[0-9]*$')]],
        location: ['', [Validators.required]],
        // consultationFee: ['', [Validators.required]],
        aboutAdvisor: ['', [Validators.required]],
        advisorImg: [null], // Will hold the File object
        assignedProperty: [[]],
        role: [this.selectedRole, [Validators.required]],
        password: [null, [Validators.required, Validators.minLength(8)]],
        c_pass: [null, [Validators.required]],
        isApproved: [true, [Validators.required]],
        state: ['Approved', [Validators.required]],
      },
      { validators: this.passwordMatchValidator }
    );
  }

  loadAdvisor() {
    const advisorData = this.advisor();
    this.advisorForm.patchValue({
      ...advisorData,
      isUserType: 'Premium',
    });
  }

  onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      this.advisorForm.patchValue({ advisorImg: file });
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreview = reader.result;
      };
      reader.readAsDataURL(file);
    }
  }

  filterResults(query: string) {
    if (!query) {
      this.filteredProperties = this.properties;
      return;
    }
    const searchStr = query.toLowerCase();
    this.filteredProperties = this.properties.filter(
      (p) =>
        p.name.toLowerCase().includes(searchStr) ||
        p.location.toLowerCase().includes(searchStr)
    );
  }

  toggleProperty(property: Property) {
    const currentProps = this.advisorForm.get('assignedProperty')?.value || [];
    const exists = currentProps.some((p: any) => p._id === property._id);

    if (!exists) {
      const updatedProps = [
        ...currentProps,
        { _id: property._id, name: property.name },
      ];
      this.advisorForm.patchValue({ assignedProperty: updatedProps });
      this.selectedProperties.push(property);
      this.filteredProperties = this.properties;
    }
  }

  removeProperty(propertyId: string) {
    const currentProps = this.advisorForm.get('assignedProperty')?.value || [];
    const updatedProps = currentProps.filter((p: any) => p._id !== propertyId);
    this.advisorForm.get('assignedProperty')?.setValue(updatedProps);
    this.selectedProperties = this.selectedProperties.filter(
      (p: any) => p._id !== propertyId
    );
  }
  onSubmit() {
    if (this.advisorForm.valid) {
      this.loaderService.show();
      const formData = new FormData();
      Object.keys(this.advisorForm.controls).forEach((key) => {
        const value = this.advisorForm.get(key)?.value;
        if (key === 'advisorImg') {
          formData.append('advisorImg', value);
        } else if (key === 'assignedProperty') {
          formData.append(key, JSON.stringify(value));
        } else if (key === 'role') {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      });
      if(this.mode === 'edit'){
        this._AS.updateAdvisor(this.advisorId,formData).subscribe({
          next: (res: any) => {
            if (res.status) {
              this.isOnboard.set(true);
              this.advisorForm.reset();
              this.selectedProperties = [];
              this.imagePreview = null;
              this.loaderService.hide();
            }
          },
          error: (err) =>
            console.error('Upload failed', err, this.loaderService.hide()),
        });
      }else{
      this._AS.addAdvisor(formData).subscribe({
        next: (res: any) => {
          if (res.status) {
            this.isOnboard.set(true);
            this.advisorForm.reset();
            this.selectedProperties = [];
            this.imagePreview = null;
            this.loaderService.hide();
          }
        },
        error: (err:any) => {
          this._NS.warning(err.error.error.message),
          console.error('Upload failed', err, this.loaderService.hide())
        }
      });
    }
    } else {
      this.loaderService.hide();
      this.advisorForm.markAllAsTouched();
    }
  }
  navigateToAdvisorList() {
    this.isOnboard.set(false);
    this.route.navigate(['admin/advisor']);
  }

  selectRole(role: any) {
    this.selectedRole = role;
    // This binds the role
    this.advisorForm.patchValue({
      role: role,
    });
    this.advisorForm.get('role')?.markAsTouched();
  }

  togglePermission(key: string) {
    this.selectedRole.permissions[key] = !this.selectedRole.permissions[key];
  }

  // Helper to format permission keys into readable labels
  formatLabel(key: string): string {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, (str) => str.toUpperCase());
  }
  passwordMatchValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const password = control.get('password');
    const confirmPassword = control.get('c_pass');

    // If they don't match, set an error on the confirmPassword control specifically
    if (
      password &&
      confirmPassword &&
      password.value !== confirmPassword.value
    ) {
      confirmPassword.setErrors({ isMatching: true });
      return { passwordMismatch: true };
    } else {
      // If they match, clear the specific error
      // Note: this will remove other errors like 'required', so be careful
      return null;
    }
  };
}
